import "./NewsAPIKeyEditView.scss";
import { debounce, flatten, uniqBy } from "lodash";
import ImagePreview from "@/components/Common/ImagePreview/ImagePreview.vue";
import { confirmWarningConfig } from "@/configuration/confirmConfigurations";
import {
  destroyFile,
  initGlobalCopyToClipboard,
  escapeHTML
} from "@/helpers/commonHelper";
import FallbackPictureCopyrightEditView from "@/components/Common/FallbackPictureCopyrightEditView/FallbackPictureCopyrightEditView.vue";
import ColorPickerButton from "@/components/Common/ColorPickerButton/ColorPickerButton.vue";
import LogOverview from "@/components/Common/LogOverview/LogOverview.vue";
import InfoDialog from "@/components/Common/InfoDialog/InfoDialog.vue";
import VuePrism from "vue-prism";
import "prismjs/themes/prism-okaidia.min.css";
import {
  getDetailHTML,
  getListHTML,
  getTeaserHTML,
  getSingleTeaserHTML,
  getNewsIssuesListHTML,
  getReportWidgetHTML
} from "@/helpers/newsHelper";
import { uniq } from "lodash/array";

const env = require(`@/environments/${process.env.VUE_APP_ENV_PATH}`);

// prettier-ignore
// eslint-disable-next-line no-undef
const { ClassicEditor, Essentials, Paragraph, Bold, Italic, Link, RemoveFormat, FindAndReplace, SourceEditing } = CKEDITOR;

export default {
  name: "NewsAPIKeyEditView",
  data: () => ({
    featureText: "",
    isValid: false,
    apiKeyPermissions: [
      { value: "edit", title: "Bearbeiten" },
      { value: "view", title: "Sehen" },
      { value: "none", title: "Keine" }
    ],
    sortDirection: [
      { value: "asc", title: "Aufsteigend" },
      { value: "desc", title: "Absteigend" }
    ],
    sortProperties: [
      { value: "title", title: "Titel" },
      { value: "newsDate", title: "Datum der Nachricht" },
      { value: "web_release_date", title: "Web-Veröffentlichungsdatum" },
      { value: "web_expire_date", title: "Enddatum" },
      { value: "web_archive_date", title: "Archivdatum" },
      { value: "author", title: "Autor" }
    ],
    cardStyles: [
      { value: "simple", title: "Simpel" },
      { value: "standard", title: "Standard" },
      { value: "grid", title: "Grid" },
      { value: "list", title: "Liste" },
      { value: "listAlignedRight", title: "Liste rechtsbündig" }
    ],
    topicSelectionStyles: [
      { value: "cloud", title: "Wolke" },
      { value: "select", title: "DropDown-Menü" }
    ],
    topicSelectionItems: [
      { value: "features", title: "Merkmale" },
      { value: "keywords", title: "Schlagworte" },
      { value: "pois", title: "POIs" }
    ],
    overlayModes: [
      { value: "dark", title: "Dark-Mode" },
      { value: "light", title: "Light-Mode" },
      { value: "overlay", title: "Overlay-Mode" }
    ],
    featuresStyles: [
      { value: "chips", title: "Chips" },
      { value: "icons", title: "Icons" }
    ],
    showIframePreview: {
      list: false,
      teaser: false,
      singleTeaser: false,
      newsIssuesList: false,
      reportWidget: false
    },
    principalLabelsKey: 0,
    refreshIframePreview: {
      list: 0,
      teaser: 0,
      singleTeaser: 0,
      newsIssuesList: 0,
      reportWidget: 0
    },
    mailTextEditorsKey: 0,
    editor: ClassicEditor,
    editorUrl: "https://cdn.eberl-online.cloud/ckeditor5/ckeditor5.js",
    mailTextEditorConfig: {
      licenseKey: "GPL",
      language: "de",
      shouldNotGroupWhenFull: true,
      plugins: [
        Essentials,
        Paragraph,
        Bold,
        Italic,
        Link,
        RemoveFormat,
        SourceEditing,
        FindAndReplace
      ],
      toolbar: [
        "undo",
        "redo",
        "|",
        "findAndReplace",
        "|",
        "bold",
        "italic",
        "removeFormat",
        "|",
        "link",
        "unlink"
      ],
      autoParagraph: false
    }
  }),
  props: ["apiKey", "channels"],
  created() {
    this.updateMailTextEditorToolbar();
  },
  async mounted() {
    try {
      await this.$store.dispatch("getAllUsers");
      await this.$store.dispatch("getFeatureGroupsForNews");
    } catch (ex) {
      await this.$store.dispatch("createError", ex);
    }
    this.initGlobalCopyToClipboard();
  },
  watch: {
    isAdmin() {
      this.updateMailTextEditorToolbar();
    }
  },
  computed: {
    rssFeedUrl() {
      return `${env.news_api_client_url}news/rss?apiKey=${this.apiKey.apiKey}`;
    },
    isAdmin() {
      return this.$store.state.currentUser?.scopes.includes("role:admin");
    },
    isReadOnly() {
      if (this.isAdmin) {
        return false;
      }
      if (this.apiKey.userPermission === "edit") {
        return false;
      }
      return true;
    },
    outputs() {
      const defaultValue = [
        { value: "", title: "Immer Allgemeine Beschreibung" }
      ];
      return defaultValue.concat(this.channels);
    },
    users() {
      const users = this.$store.state.users;
      if (!users?.length) {
        return [];
      }
      return users.filter(
        user => user.principal_id === this.currentPrincipal?._id
      );
    },
    principals() {
      return this.$store.state.principals;
    },
    principalLabels: {
      get() {
        const currentPrincipalIncluded = this.apiKey.principalLabels?.find(
          principal => principal.id === this.currentPrincipal?.id
        );
        if (!currentPrincipalIncluded) {
          if (!this.apiKey.principalLabels?.length)
            this.apiKey.principalLabels = [
              {
                id: this.currentPrincipal?.id,
                name: this.currentPrincipal.name,
                label: "",
                color: "",
                backgroundColor: ""
              }
            ];
          else
            this.apiKey.principalLabels.push({
              id: this.currentPrincipal?.id,
              name: this.currentPrincipal.name,
              label: "",
              color: "",
              backgroundColor: ""
            });
        } else {
          this.apiKey.subPrincipals.forEach(principal => {
            this.apiKey.principalLabels.push({
              id: principal,
              name: this.principals.find(p => p.id === principal)?.name,
              label: "",
              color: "",
              backgroundColor: ""
            });
          });
        }
        return uniqBy(this.apiKey.principalLabels, "id");
      },
      set(value) {
        this.apiKey.principalLabels = this.apiKey.principalLabels.filter(
          principal =>
            principal.label?.length &&
            (principal.color?.length || principal.backgroundColor?.length)
        );
        this.apiKey.principalLabels = uniqBy(value, "id");
      }
    },
    featureGroups() {
      return this.$store.state.featureGroupsForNews;
    },
    features() {
      const features = flatten(this.featureGroups?.map(fg => fg.features));
      features.sort((a, b) => a.title.localeCompare(b.title));
      return features;
    },
    fallbackPicture: {
      get() {
        return this.apiKey.fallbackPicture;
      },
      set(newVal) {
        this.pictureArray = [];
        this.pictureArray.push(newVal);
        this.apiKey.fallbackPicture = newVal;
      }
    },
    // Update computed property only when 'refreshIframePreview' is increased in debounced function 'setCodePreview'
    // as to not reload the iframe for every color single change (e.g. by selecting a color via the color-picker canvas).
    iframeSrcListDebounced() {
      if (++this.refreshIframePreview.list) {
        return this.getIframeSrc("news/");
      }
    },
    iframeSrcTeaserDebounced() {
      if (++this.refreshIframePreview.teaser) {
        return this.getIframeSrc("teaser/");
      }
    },
    iframeSrcSingleTeaserDebounced() {
      if (++this.refreshIframePreview.singleTeaser) {
        return this.getIframeSrc("single-teaser/");
      }
    },
    iframeSrcNewsIssuesListDebounced() {
      if (++this.refreshIframePreview.newsIssuesList) {
        return this.getNewsIssuesIframeSrc();
      }
    },
    iframeSrcReportWidgetDebounced() {
      if (++this.refreshIframePreview.reportWidget) {
        return this.getReportWidgetIframeSrc();
      }
    },
    currentPrincipal() {
      return this.$store.state.selectedPrincipal;
    },
    logUrl() {
      return `${env.news_api_client_url}logs/${this.apiKey._id}`;
    },
    defaultAPIKeyMailTextBeforeData() {
      return `<p>Sehr geehrte Damen und Herren,</p>
<p>folgende Nachricht haben wir f&uuml;r Sie aufgenommen.</p>`;
    },
    defaultAPIKeyMailTextAfterData() {
      let furtherQuestions = "";
      if (this.apiKey.adminMailAddresses?.length) {
        furtherQuestions = `
    <p>
        Sollten Sie Fragen oder Anpassungsw&uuml;nsche haben, wenden Sie sich bitte an:
        <b>
          <a href="mailto:${this.apiKey.adminMailAddresses[0]}">
             ${this.apiKey.adminMailAddresses[0]}
          </a>
        </b>
    </p>
    <p>&#160;</p>`;
      }

      return `${furtherQuestions}
    <p>Vielen Dank!</p>
    <p>
        Mit freundlichen Gr&uuml;&szlig;en<br/>
        <b>${this.apiKey.mailSenderName}</b>
    </p>`;
    }
  },
  methods: {
    debounce,
    initGlobalCopyToClipboard,
    escapeHTML,
    getListHTML,
    getTeaserHTML,
    getSingleTeaserHTML,
    getDetailHTML,
    getNewsIssuesListHTML,
    getReportWidgetHTML,
    updateMailTextEditorToolbar() {
      const hasSourceEditing = this.mailTextEditorConfig.toolbar.includes(
        "sourceEditing"
      );

      if (this.isAdmin && !hasSourceEditing) {
        this.mailTextEditorConfig.toolbar.push("sourceEditing");
        this.mailTextEditorsKey++;
        return;
      }

      if (!this.isAdmin && hasSourceEditing) {
        this.mailTextEditorConfig.toolbar = this.mailTextEditorConfig.toolbar.filter(
          toolbarItem => toolbarItem !== "sourceEditing"
        );
        this.mailTextEditorsKey++;
      }
    },
    setMailTextEditorData(key, value) {
      if (key === "before") {
        this.$set(this.apiKey, "mailTextBeforeData", value);
        return;
      }
      if (key === "after") {
        this.$set(this.apiKey, "mailTextAfterData", value);
      }
    },
    showMailTextEditorPlaceholder(key) {
      if (key === "before") {
        return this.isHtmlContentEmpty(this.apiKey?.mailTextBeforeData);
      }
      if (key === "after") {
        return this.isHtmlContentEmpty(this.apiKey?.mailTextAfterData);
      }
      return false;
    },
    isHtmlContentEmpty(value) {
      if (!value?.length) {
        return true;
      }
      return !value
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, " ")
        .trim().length;
    },
    async copyRssFeedUrlToClipboard() {
      try {
        await this.$emit("saveKey");
        await navigator.clipboard.writeText(this.rssFeedUrl);
        this.$store.dispatch(
          "createSuccess",
          "RSS-Feed URL wurde in die Zwischenablage kopiert."
        );
      } catch (e) {
        this.$store.dispatch(
          "createError",
          "Kopieren in die Zwischenablage ist fehlgeschlagen."
        );
      }
    },
    getParamsForIframeSrc(colorObj) {
      let c = "?";
      const paramsObj = {
        apiKey: this.apiKey.apiKey,
        ...colorObj
      };
      let params = "";
      for (const [key, value] of Object.entries(paramsObj)) {
        params += `${c}${key}=${encodeURIComponent(value)}`;
        c = "&";
      }
      return params;
    },
    getIframeSrc(listOrTeaser) {
      if (!listOrTeaser.endsWith("/")) {
        listOrTeaser += "/";
      }
      return `${
        env.news_widget_api_url
      }${listOrTeaser}${this.getParamsForIframeSrc(this.apiKey.color)}`;
    },
    setCardBackgroundColor(color) {
      this.apiKey.cardBackgroundColor = color;
      this.$nextTick(() => {
        this.$forceUpdate();
        this.setCodePreview();
      });
    },
    setPrincipalLabel(value, index) {
      this.principalLabels[index].label = value;
      this.principalLabels = uniqBy(this.principalLabels, "id");
      this.$forceUpdate();
    },
    setPrincipalColor(value, index) {
      this.principalLabels[index].color = value;
      this.principalLabels = uniqBy(this.principalLabels, "id");
      this.$forceUpdate();
    },
    setPrincipalBackgroundColor(value, index) {
      this.principalLabels[index].backgroundColor = value;
      this.principalLabels = uniqBy(this.principalLabels, "id");
      this.$forceUpdate();
    },
    addSubprincipals(value) {
      let principals = value;
      principals.push(this.currentPrincipal?.id);
      principals = uniq(principals);

      this.principalLabels = this.principalLabels.filter(label =>
        principals.includes(label.id)
      );
      principals.forEach(principal => {
        if (!this.principalLabels.find(label => label.id === principal)) {
          this.principalLabels.push({
            id: principal,
            name: this.principals.find(p => p.id === principal)?.name,
            label: "",
            color: "",
            backgroundColor: ""
          });
        }
      });
      this.apiKey.subPrincipals = uniq(value);
      this.principalLabels = this.apiKey.principalLabels;
      this.principalLabelsKey++;
    },
    getNewsIssuesIframeSrc() {
      return `${
        env.news_issues_widget_api_url
      }news-issues${this.getParamsForIframeSrc(
        this.apiKey.newsIssuesWidget.color
      )}`;
    },
    getReportWidgetIframeSrc() {
      return `${env.news_report_widget_api_url}${this.getParamsForIframeSrc(
        this.apiKey.color
      )}`;
    },
    getIframeHTML(listOrTeaser) {
      return `<iframe src="${this.getIframeSrc(listOrTeaser)}"></iframe>`;
    },
    getNewsIssuesIframeHTML() {
      return `<iframe src="${this.getNewsIssuesIframeSrc()}"></iframe>`;
    },
    getReportWidgetIframeHTML() {
      return `<iframe src="${this.getReportWidgetIframeSrc()}"></iframe>`;
    },
    getPreviewOuterHTML(innerHTML) {
      const randomId = (Math.random() + 1).toString(36).substring(7);
      const copyButtonHTML = `<p class="copy-code-preview"
      onClick="copyCodePreviewToClipboard('${randomId}');"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M19 21H8V7h11m0-2H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2m-3-4H4a2 2 0 0 0-2 2v14h2V3h12V1Z"/></svg>
        <span>Kopieren</span>
      </p>`;
      return `${copyButtonHTML}<pre class="language-markup" id="${randomId}"><code>${this.escapeHTML(
        innerHTML
      )}</code></pre>`;
    },
    setColorAndCodePreview(colorObj, colorKey, colorValue) {
      this.$set(colorObj, colorKey, colorValue);
      this.$nextTick(() => {
        this.$forceUpdate();
        this.setCodePreview();
      });
    },
    setCodePreview: debounce(function() {
      const codePreviewContainers = document.querySelectorAll(
        ".widget-code-preview"
      );
      if (!codePreviewContainers?.length) {
        return;
      }
      Array.from(codePreviewContainers).forEach(codePreviewContainer => {
        const codePreviewElement = codePreviewContainer.querySelector("pre");

        const codePreviewFor =
          codePreviewContainer.attributes["data-code-preview-for"].value;
        let innerHTML;
        switch (codePreviewFor) {
          case "list":
            innerHTML = this.getListHTML(this.apiKey);
            break;
          case "teaser":
            innerHTML = this.getTeaserHTML(this.apiKey);
            break;
          case "single-teaser":
            innerHTML = this.getSingleTeaserHTML(this.apiKey);
            break;
          case "detail":
            innerHTML = this.getDetailHTML(this.apiKey);
            break;
          case "iframe-list":
            innerHTML = this.getIframeHTML("news");
            this.refreshIframePreview.list++;
            break;
          case "iframe-teaser":
            innerHTML = this.getIframeHTML("teaser");
            this.refreshIframePreview.teaser++;
            break;
          case "iframe-single-teaser":
            innerHTML = this.getIframeHTML("single-teaser");
            this.refreshIframePreview.singleTeaser++;
            break;
          case "news-issues-list":
            innerHTML = this.getNewsIssuesListHTML(this.apiKey);
            break;
          case "reportWidget":
            innerHTML = this.getReportWidgetHTML(this.apiKey);
            break;
          case "iframe-news-issues-list":
            innerHTML = this.getNewsIssuesIframeHTML("news-issues-list");
            this.refreshIframePreview.newsIssuesList++;
            break;
          case "iframe-reportWidget":
            innerHTML = this.getReportWidgetIframeHTML();
            this.refreshIframePreview.reportWidget++;
            break;
          default:
            throw new Error("can not set code preview");
        }

        if (codePreviewElement) {
          codePreviewElement.remove();
        }
        codePreviewContainer.lastChild.innerHTML = this.getPreviewOuterHTML(
          innerHTML
        );
      });

      window.Prism.highlightAll();
    }, 1000),
    clearInput(text, filteredItems) {
      if (filteredItems?.length === 1) this[text] = "";
    },
    checkFallbackImage() {
      return (
        this.apiKey?.fallbackPicture?.url ||
        this.apiKey?.fallbackPicture?.dataUrl
      );
    },
    async addDropFile(e) {
      if (this.isReadOnly) {
        return;
      }
      if (typeof this.apiKey.fallbackPicture !== "object")
        this.apiKey.fallbackPicture = {};
      const file = e?.dataTransfer?.files ?? e ?? false;
      if (file) {
        file.dataUrl = await this.readFileUrl(file);
        file.title = file.name;
        this.apiKey.fallbackPicture = file;
        await this.$emit("saveKey");
      } else this.apiKey.fallbackPicture = {};
    },
    async confirmRemoveFallbackImage() {
      if (this.isReadOnly) {
        return;
      }
      const isConfirmed = await this.$confirm(
        "Sind Sie sicher, dass Sie das Fallback-Bild löschen möchten?",
        confirmWarningConfig
      );
      if (isConfirmed) {
        await this.removeFile();
      }
    },
    async removeFile() {
      const deleted = await destroyFile("news", this.apiKey.fallbackPicture.id);
      if (!deleted) {
        await this.$store.dispatch("createError", {
          text: `Fehler beim löschen des Fallback-Bildes auf dem Dateisystem(S3).
            (Das Fallback-Bild erscheint trotz dessen nicht mehr).`
        });
      }
      this.apiKey.fallbackPicture = {};
      await this.$emit("saveKey");
    },
    readFileUrl(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = ({ target }) => {
          if (target?.result) resolve(target.result);
          else reject("Unable to read File");
        };
        reader.readAsDataURL(file);
      });
    }
  },
  components: {
    InfoDialog,
    ImagePreview,
    FallbackPictureCopyrightEditView,
    ColorPickerButton,
    VuePrism,
    LogOverview
  }
};
